import { ReactNode } from 'react';
import { Trans } from 'react-i18next';

import { calculateProjectCPI, ProjectService } from 'entities';

import { Button, Column, LabeledInput, Modal, Row } from 'components/Blocks';
import { ArrowRightIcon, CircleDollarIcon, WarningIcon } from 'components/Icons';
import { PercentageVariation } from 'components/PercentageVariation';

import { useMultiStateV2 } from 'hooks';
import { useLang } from 'hooks/lang';

import { getPriceDeltaPercentage } from './PriceChange';
import { formatCurrency } from 'utils/text';

import { mergeServices, type RepriceService } from './utils';

export interface ConfirmRepriceButtonProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick: () => Promise<any>;

  disabled: boolean;
  loading: boolean;
  currentServices: ProjectService[];
  repricedServices: RepriceService[];
  currency: string;
  footer?: ReactNode;
}

const NON_NORMAL_PERCENTAGE = 37;

function validateDelta(delta: number) {
  return Math.abs(delta) < NON_NORMAL_PERCENTAGE;
}

export function ConfirmRepriceButton({
  onClick,
  currentServices,
  repricedServices,
  currency,
  footer,
  ...buttonProps
}: ConfirmRepriceButtonProps) {
  const { t } = useLang();

  const [{ text, isOpen }, { dispatch, resetDefault, getSetter }] = useMultiStateV2({
    text: '',
    isOpen: false,
  });

  const { delta, currentTotal, newTotal } = getPriceDeltaPercentage(
    currentServices,
    repricedServices,
  );

  const percentage = delta.toFixed(2);

  const expectedText = t(delta > 0 ? 'confirm-%-increase' : 'confirm-%-decrease', { percentage });

  const textOk = expectedText === text;

  const handleClick = () => {
    if (validateDelta(delta)) return onClick();

    dispatch({ text: '', isOpen: true });
  };

  const confirmReprice = () => {
    if (!textOk) return;

    onClick();
  };

  return (
    <>
      <Button onClick={handleClick} {...buttonProps}>
        <CircleDollarIcon />
        {t('update-prices')}
      </Button>

      <Modal isOpen={isOpen} close={resetDefault} width="min(90vw, 560px)" gap="6">
        <Column alignItems="center" gap="2">
          <div className="rounded-full bg-destructive/30 p-3 text-destructive">
            <WarningIcon size={32} />
          </div>

          <span className="text-2xl font-semibold text-destructive">
            {t('warning-price-change')}
          </span>
        </Column>

        <div className="mx-auto w-fit text-5xl [&_svg]:size-10">
          <PercentageVariation current={currentTotal} newValue={newTotal} />
        </div>

        <Column gap="2">
          <Row justifyContent="center" className="font-medium">
            <span>{formatCurrency(currentTotal, currency)}</span>
            <ArrowRightIcon />
            <span className="font-bold">{formatCurrency(newTotal, currency)}</span>
          </Row>

          <Row justifyContent="center" className="font-medium">
            <span className="-ml-12 font-semibold text-primary">CPI</span>
            <span>{formatCurrency(calculateProjectCPI(currentServices), currency)}</span>

            <ArrowRightIcon />

            <span className="font-bold">
              {formatCurrency(
                calculateProjectCPI(mergeServices(repricedServices, currentServices)),
                currency,
              )}
            </span>
          </Row>
        </Column>

        <Column gap="2">
          <p>
            {t('reprice-will-modify-by')}{' '}
            <strong className={delta > 0 ? 'text-success-light' : 'text-destructive-light'}>
              {percentage}%
            </strong>
            .
          </p>

          <p>{t('unusual-reprice-amount')}</p>
        </Column>

        <LabeledInput
          onPaste={(e) => e.preventDefault()}
          value={text}
          onChangeDirect={getSetter('text')}
          placeholder={expectedText}
          label={
            <span className="select-none font-normal italic">
              <Trans i18nKey="please-write-x-to-confirm" values={{ text: expectedText }} />
            </span>
          }
        />

        {footer}

        <Button disabled={!textOk} onClick={confirmReprice} loading={buttonProps.loading}>
          <CircleDollarIcon />
          {t('update-prices')}
        </Button>
      </Modal>
    </>
  );
}
